import { IconButton } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../../../utils/validation';
import type { MedicationRecord, Medications } from './medications';
import { translateKey } from '../../../../../../translate';
import { DateInput, NullableInputText, TableData, TableRow } from '../../../../../elements/common';
import { medicationsErrorPrefix } from '../../../validateMedications';

interface IMedicationsAddEditTableRowProps {
    errors?: ValidationErrors;
    index?: number;
    medication: MedicationRecord;
    medicationKey: string;
    medications: Medications;
    onChange: (medications: Medications) => Promise<void>;
    validateMedications?: () => Promise<void>;
}

export function MedicationsAddEditTableRow(props: IMedicationsAddEditTableRowProps) {
    return (
        <TableRow key={props.medicationKey}>
            <TableData className="has-textinput">
                <NullableInputText
                    autoFocus={!!props.medication.name}
                    automation={`medicationName_${props.index}`}
                    error={props.errors?.get(`${medicationsErrorPrefix + props.index}.name`)}
                    onChange={async (value) =>
                        await props.onChange(props.medications.setIn([props.medicationKey, 'name'], value))
                    }
                    onDoValidate={props.validateMedications}
                    placeholder={translateKey('medications.medicationNamePlaceholder')}
                    value={props.medication.name}
                />
            </TableData>
            <TableData className="has-textinput">
                <NullableInputText
                    automation={`medicationPrescription_${props.index}`}
                    error={props.errors?.get(`${medicationsErrorPrefix + props.index}.dosageInstruction`)}
                    onChange={async (value) =>
                        await props.onChange(props.medications.setIn([props.medicationKey, 'dosageInstruction'], value))
                    }
                    onDoValidate={props.validateMedications}
                    placeholder={translateKey('medications.medicationPresciptionPlaceholder')}
                    value={props.medication.dosageInstruction}
                />
            </TableData>
            <TableData className="has-datepicker">
                <DateInput
                    id={`startDate_${props.index}`}
                    maxDate={props.medications.getIn([props.medicationKey, 'endDate'])}
                    onChange={async (value) =>
                        await props.onChange(props.medications.setIn([props.medicationKey, 'startDate'], value))
                    }
                    placeholder={translateKey('common.datePlaceholder')}
                    value={props.medication.startDate}
                />
            </TableData>
            <TableData className="has-datepicker">
                <DateInput
                    id={`endDate_${props.index}`}
                    minDate={props.medications.getIn([props.medicationKey, 'startDate'])}
                    onChange={async (value) =>
                        await props.onChange(props.medications.setIn([props.medicationKey, 'endDate'], value))
                    }
                    placeholder={translateKey('common.datePlaceholder')}
                    value={props.medication.endDate}
                />
            </TableData>
            <TableData align="right">
                <IconButton
                    icon="delete"
                    onClick={async () => {
                        await props.onChange(props.medications.delete(props.medicationKey));
                        await props.validateMedications?.();
                    }}
                    size="small"
                    variant="default"
                />
            </TableData>
        </TableRow>
    );
}
